import { SystemEntity } from '@erp/shared-types';
import type { TaskResponse } from '@erp/rpc';

export interface TaskEntityRef {
  entityType: SystemEntity;
  entityId: string;
}

const SYSTEM_ENTITY_VALUES = new Set<string>(Object.values(SystemEntity));

function isSystemEntity(value: unknown): value is SystemEntity {
  return typeof value === 'string' && SYSTEM_ENTITY_VALUES.has(value);
}

function isUuid(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value,
    )
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseRef(candidate: unknown): TaskEntityRef | null {
  if (!isRecord(candidate)) return null;
  const type = candidate.entityType ?? candidate.type;
  const id = candidate.entityId ?? candidate.id;
  if (!isSystemEntity(type) || !isUuid(id)) return null;
  return { entityType: type, entityId: id };
}

export function extractEntityRefs(task: TaskResponse): TaskEntityRef[] {
  const metadata = task.metadata;
  if (!metadata) return [];

  const refs: TaskEntityRef[] = [];

  const singular = parseRef(metadata);
  if (singular) refs.push(singular);

  const list = metadata.entityRefs;
  if (Array.isArray(list)) {
    for (const item of list) {
      const ref = parseRef(item);
      if (
        ref &&
        !refs.some(
          (r) => r.entityId === ref.entityId && r.entityType === ref.entityType,
        )
      ) {
        refs.push(ref);
      }
    }
  }

  return refs;
}
